import {
  CloseOutlined,
  ExclamationCircleFilled,
  MoreOutlined,
  PlusCircleFilled,
} from '@ant-design/icons';
import { Button, Dropdown, Menu, Row, Table, Tooltip, message } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/lib/table';
import type { FilterValue, SorterResult } from 'antd/lib/table/interface';
import { FC, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import {
  deleteApiKey,
  fetchApiKeysPaged,
  revokeApiKey,
} from '../../api/apiKey';
import {
  DeactivateAPIKeyInstanceForm,
  GeneratedAPIKeyInstance,
} from '../../components';
import APIKeyFormModal, {
  APIKeyFormMode,
} from '../../components/Forms/APIKeys/FormModal';
import {
  CalendarFilterIcon,
  DateRangeFilterDropdown,
  SearchFilterIcon,
  SingleDateFilterDropdown,
  StatusFilterDropdown,
  StatusFilterIcon,
  TextFilterDropdown,
} from '../../components/Forms/APIKeyInstance/Filters';
import { APIKeyDetails } from '../../constants/types';
import {
  ApiKeyFilters,
  DEFAULT_SORT,
  DEFAULT_STATUS_FILTER,
  LegacyAPIKeyDetails,
  SortKey,
  buildApiKeysQuery,
  compactMaskedKey,
  getExpiringKeyMessage,
  getStatusLabelId,
  normalizeApiKey,
  truncateText,
} from '../../helpers/apiKey';
import { useModal } from '../../hooks/useModal';
import {
  diffToLocalTimeZone,
  formatDateToLocalTimeZone,
  formatDateToUTC,
} from '../../utils/dateFormatter';
import { formatErrorMessage } from '../../utils/errorMessage';
import {
  APIKeyWarning,
  ApiKeyDatePickerGlobalStyles,
  ApiKeyTableWrapper,
  MaskedKeyCell,
  StyledModal,
  Wrapper,
} from '../styles';

const PageContainer = styled.div`
  padding-top: 12px;
`;

const HeaderActionsRow = styled(Row)`
  padding: 0 30px 12px;
`;

const ClearFilterButton = styled(Button)`
  margin-right: 12px;
  min-width: auto !important;
  width: auto !important;
  font-weight: normal;
`;

const NewApiKeyButton = styled(Button)`
  min-width: auto !important;
  width: auto !important;
  font-weight: normal;
`;

const APIKey: FC = () => {
  const intl = useIntl();
  const { showDeleteConfirm } = useModal();

  const [data, setData] = useState<APIKeyDetails[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [refreshTick, setRefreshTick] = useState(0);
  const [sort, setSort] = useState<{ key: SortKey; order: 'asc' | 'desc' }>(
    DEFAULT_SORT,
  );
  const [filters, setFilters] = useState<ApiKeyFilters>({
    status: DEFAULT_STATUS_FILTER,
  });

  // Modal state
  const [formMode, setFormMode] = useState<APIKeyFormMode | null>(null);
  const [formSource, setFormSource] = useState<APIKeyDetails | undefined>();
  const [generatedKey, setGeneratedKey] = useState<string>('');
  const [deactivateTarget, setDeactivateTarget] =
    useState<APIKeyDetails | null>(null);
  const [highlightedId, setHighlightedId] = useState<string | undefined>();

  useEffect(() => {
    if (!highlightedId) return;
    const timeoutId = window.setTimeout(() => {
      setHighlightedId(undefined);
    }, 5000);
    return () => window.clearTimeout(timeoutId);
  }, [highlightedId]);

  useEffect(() => {
    setLoading(true);
    fetchApiKeysPaged(buildApiKeysQuery(page, pageSize, sort, filters))
      .then(res => {
        const normalizedData = (
          (res.data.result ?? []) as LegacyAPIKeyDetails[]
        ).map(normalizeApiKey);
        setData(normalizedData);
        setTotal(res.data.count ?? 0);
      })
      .catch((error: any) => {
        message.error(formatErrorMessage(error?.data?.errorCode), 2);
      })
      .finally(() => setLoading(false));
  }, [page, pageSize, sort, filters, refreshTick]);

  const refresh = () => setRefreshTick(tick => tick + 1);
  const isStatusFilterActive = (filters.status?.length ?? 0) > 0;
  const hasActiveFilters =
    !!filters.maskedKey ||
    !!filters.name ||
    !!filters.activeFromAfter ||
    !!filters.expireAtBefore ||
    !!filters.updatedRange?.length ||
    isStatusFilterActive;
  const canClearFilters =
    hasActiveFilters ||
    sort.key !== DEFAULT_SORT.key ||
    sort.order !== DEFAULT_SORT.order ||
    page !== 1;

  const clearAllFilters = () => {
    setFilters({ status: [] });
    setSort(DEFAULT_SORT);
    setPage(1);
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    tableFilters: Record<string, FilterValue | null>,
    sorter: SorterResult<APIKeyDetails> | SorterResult<APIKeyDetails>[],
  ) => {
    setPage(pagination.current ?? 1);
    setPageSize(pagination.pageSize ?? 20);

    const next: ApiKeyFilters = {};
    next.maskedKey = (tableFilters.masked?.[0] as string) ?? undefined;
    next.name = (tableFilters.name?.[0] as string) ?? undefined;
    next.activeFromAfter = (tableFilters.activeFrom?.[0] as any) ?? undefined;
    next.expireAtBefore = (tableFilters.expireAt?.[0] as any) ?? undefined;
    next.updatedRange = tableFilters.updatedAt as any;
    next.status =
      (tableFilters.status as APIKeyDetails['status'][]) ?? undefined;
    setFilters(next);

    const single = Array.isArray(sorter) ? sorter[0] : sorter;
    if (single?.field && single?.order) {
      const fieldMap: Record<string, SortKey> = {
        name: 'name',
        activeFrom: 'active_from',
        expireAt: 'expires_at',
        updatedAt: 'updated',
        lastUsedAt: 'last_used_on',
      };
      const key = fieldMap[single.field as string];
      if (key) {
        setSort({
          key,
          order: single.order === 'descend' ? 'desc' : 'asc',
        });
      }
    } else {
      // Default sort
      setSort(DEFAULT_SORT);
    }
  };

  const onCreatedOrUpdated = (
    result: { id?: string; apiKey?: string },
    label: string,
  ) => {
    if (result.id) setHighlightedId(result.id);
    if (result.apiKey) setGeneratedKey(result.apiKey);
    setFormMode(null);
    setFormSource(undefined);
    setSort({ key: 'updated', order: 'desc' });
    setPage(1);
    setRefreshTick(tick => tick + 1);
    message.success(label, 2);
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget?.id) return;
    try {
      await revokeApiKey(deactivateTarget.id);
      message.success(
        intl.formatMessage({ id: 'ApiKeys.modal.apiKeys.deactivated' }),
        2,
      );
      setDeactivateTarget(null);
      refresh();
    } catch (error: any) {
      message.error(formatErrorMessage(error?.data?.errorCode), 2);
    }
  };

  const handleDelete = async (record: APIKeyDetails) => {
    if (!record.id) return;
    await deleteApiKey(record.id);
    message.success(intl.formatMessage({ id: 'ApiKeys.delete.success' }), 2);
    refresh();
  };

  const confirmDelete = (record: APIKeyDetails) => {
    showDeleteConfirm({
      title: (
        <span>
          {intl.formatMessage({ id: 'ApiKeys.delete.confirm.title' })}
        </span>
      ),
      deleteItemNameTitle: 'ApiKeys.groupList.labelWithColon',
      deleteItemNameValue: record.name,
      description: (
        <span>
          {intl.formatMessage({ id: 'ApiKeys.delete.confirm.description' })}
        </span>
      ),
      showDeleteInputField: true,
      okText: 'Global.delete',
      cancelText: 'Global.cancel',
      onOkAsync: async () => {
        await handleDelete(record);
      },
    });
  };

  const renderActionMenu = (record: APIKeyDetails) => (
    <Menu>
      {record.status === 'Active' && (
        <Menu.Item
          key="deactivate"
          onClick={() => setDeactivateTarget(record)}
          data-testid={`apikey-action-deactivate-${record.id}`}
        >
          <FormattedMessage id="ApiKeys.action.deactivate" />
        </Menu.Item>
      )}
      <Menu.Item
        key="update"
        onClick={() => {
          setFormSource(record);
          setFormMode('update');
        }}
        data-testid={`apikey-action-update-${record.id}`}
      >
        <FormattedMessage id="ApiKeys.action.update" />
      </Menu.Item>
      <Menu.Item
        key="rotate"
        onClick={() => {
          setFormSource(record);
          setFormMode('rotate');
        }}
        data-testid={`apikey-action-rotate-${record.id}`}
      >
        <FormattedMessage id="ApiKeys.action.rotate" />
      </Menu.Item>
      <Menu.Item
        key="delete"
        onClick={() => confirmDelete(record)}
        data-testid={`apikey-action-delete-${record.id}`}
      >
        <FormattedMessage id="ApiKeys.action.delete" />
      </Menu.Item>
    </Menu>
  );

  const columns: ColumnsType<APIKeyDetails> = [
    {
      title: intl.formatMessage({ id: 'ApiKeys.column.apiKey' }),
      dataIndex: 'masked',
      key: 'masked',
      width: 130,
      filterDropdown: props => (
        <TextFilterDropdown
          {...props}
          placeholder={intl.formatMessage({
            id: 'ApiKeys.filter.searchApiKeyPlaceholder',
          })}
        />
      ),
      filterIcon: filtered => <SearchFilterIcon filtered={filtered} />,
      filteredValue: filters.maskedKey ? [filters.maskedKey] : null,
      render: (masked: string, record: APIKeyDetails) => {
        const expiry = getExpiringKeyMessage(record);
        return (
          <MaskedKeyCell>
            <Tooltip title={masked}>
              <span>{compactMaskedKey(masked)}</span>
            </Tooltip>
            {expiry && (
              <APIKeyWarning>
                <Tooltip
                  arrowPointAtCenter
                  placement="topLeft"
                  title={intl.formatMessage(
                    { id: expiry.messageKey },
                    { days: expiry.days },
                  )}
                >
                  <ExclamationCircleFilled />
                </Tooltip>
              </APIKeyWarning>
            )}
          </MaskedKeyCell>
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'ApiKeys.form.label.keyName' }),
      dataIndex: 'name',
      key: 'name',
      width: 180,
      ellipsis: true,
      sorter: true,
      sortOrder:
        sort.key === 'name'
          ? sort.order === 'asc'
            ? 'ascend'
            : 'descend'
          : null,
      filterDropdown: props => (
        <TextFilterDropdown
          {...props}
          placeholder={intl.formatMessage({
            id: 'ApiKeys.filter.searchNamePlaceholder',
          })}
        />
      ),
      filterIcon: filtered => <SearchFilterIcon filtered={filtered} />,
      filteredValue: filters.name ? [filters.name] : null,
      render: (name: string) => (
        <Tooltip title={name}>
          <span>{truncateText(name, 20)}</span>
        </Tooltip>
      ),
    },
    {
      title: intl.formatMessage({ id: 'ApiKeys.form.label.description' }),
      dataIndex: 'description',
      key: 'description',
      width: 200,
      ellipsis: true,
      render: (description: string) => (
        <Tooltip title={description}>
          <span>{truncateText(description, 20)}</span>
        </Tooltip>
      ),
    },
    {
      title: intl.formatMessage({ id: 'ApiKeys.column.activefrom' }),
      dataIndex: 'activeFrom',
      key: 'activeFrom',
      width: 180,
      sorter: true,
      sortOrder:
        sort.key === 'active_from'
          ? sort.order === 'asc'
            ? 'ascend'
            : 'descend'
          : null,
      filterDropdown: props => <SingleDateFilterDropdown {...props} />,
      filterIcon: filtered => <CalendarFilterIcon filtered={filtered} />,
      filteredValue: filters.activeFromAfter
        ? ([filters.activeFromAfter] as any)
        : null,
      render: (date: string) => formatDateToLocalTimeZone(date),
    },
    {
      title: intl.formatMessage({ id: 'ApiKeys.column.activeuntil' }),
      dataIndex: 'expireAt',
      key: 'expireAt',
      width: 180,
      sorter: true,
      defaultSortOrder: 'ascend',
      sortOrder:
        sort.key === 'expires_at'
          ? sort.order === 'asc'
            ? 'ascend'
            : 'descend'
          : null,
      filterDropdown: props => <SingleDateFilterDropdown {...props} />,
      filterIcon: filtered => <CalendarFilterIcon filtered={filtered} />,
      filteredValue: filters.expireAtBefore
        ? ([filters.expireAtBefore] as any)
        : null,
      render: (date: string) => formatDateToLocalTimeZone(date),
    },
    {
      title: intl.formatMessage({ id: 'ApiKeys.column.status' }),
      dataIndex: 'status',
      key: 'status',
      width: 140,
      filterDropdown: props => <StatusFilterDropdown {...props} />,
      filterIcon: filtered => <StatusFilterIcon filtered={filtered} />,
      filteredValue: (filters.status as any) ?? null,
      render: (status: APIKeyDetails['status']) =>
        intl.formatMessage({ id: getStatusLabelId(status) }),
    },
    {
      title: intl.formatMessage({ id: 'ApiKeys.modal.label.dateModified' }),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 180,
      sorter: true,
      sortOrder:
        sort.key === 'updated'
          ? sort.order === 'asc'
            ? 'ascend'
            : 'descend'
          : null,
      filterDropdown: props => <DateRangeFilterDropdown {...props} />,
      filterIcon: filtered => <CalendarFilterIcon filtered={filtered} />,
      filteredValue: filters.updatedRange as any,
      render: (date: string) =>
        date ? (
          <Tooltip title={formatDateToUTC(date)}>
            {formatDateToLocalTimeZone(date)}
          </Tooltip>
        ) : (
          'N/A'
        ),
    },
    {
      title: intl.formatMessage({ id: 'ApiKeys.column.lastUsed' }),
      dataIndex: 'lastUsedAt',
      key: 'lastUsedAt',
      width: 140,
      sorter: true,
      sortOrder:
        sort.key === 'last_used_on'
          ? sort.order === 'asc'
            ? 'ascend'
            : 'descend'
          : null,
      render: (date: string) =>
        date ? (
          <Tooltip title={formatDateToUTC(date)}>
            {diffToLocalTimeZone(date).text}
          </Tooltip>
        ) : (
          'N/A'
        ),
    },
    {
      title: intl.formatMessage({ id: 'ApiKeys.column.action' }),
      key: 'actions',
      width: 95,
      align: 'center',
      fixed: 'right',
      className: 'table-action-column',
      render: (_, record) => (
        <Dropdown
          overlay={renderActionMenu(record)}
          trigger={['click']}
          placement="bottomRight"
        >
          <MoreOutlined
            className="three-dot-icon"
            data-testid={`apikey-action-menu-${record.id}`}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <PageContainer>
      <ApiKeyDatePickerGlobalStyles />
      <HeaderActionsRow justify="end">
        {canClearFilters && (
          <Tooltip
            title={intl.formatMessage({ id: 'ApiKeys.filter.resetTooltip' })}
          >
            <ClearFilterButton
              type="default"
              size="middle"
              icon={<CloseOutlined />}
              onClick={clearAllFilters}
              data-testid="apikey-clear-filters"
            >
              <FormattedMessage id="ApiKeys.filter.clearFilter" />
            </ClearFilterButton>
          </Tooltip>
        )}
        <NewApiKeyButton
          type="default"
          size="middle"
          icon={<PlusCircleFilled />}
          onClick={() => {
            setFormSource(undefined);
            setFormMode('new');
          }}
          data-testid="apikey-new"
        >
          <FormattedMessage id="ApiKeys.btn.newApikey" />
        </NewApiKeyButton>
      </HeaderActionsRow>

      <Wrapper>
        <ApiKeyTableWrapper>
          <Table<APIKeyDetails>
            rowKey="id"
            columns={columns}
            dataSource={data}
            loading={loading}
            scroll={{ x: 'max-content' }}
            rowClassName={record =>
              record.id === highlightedId ? 'highlight-row' : ''
            }
            pagination={{
              current: page,
              pageSize,
              total,
              showTotal: (totalItems, range) =>
                intl.formatMessage(
                  { id: 'ApiKeys.pagination.total' },
                  {
                    start: range[0],
                    end: range[1],
                    total: totalItems,
                  },
                ),
              showSizeChanger: true,
              showQuickJumper: true,
              position: ['bottomRight'],
            }}
            onChange={handleTableChange}
          />
        </ApiKeyTableWrapper>
      </Wrapper>

      <APIKeyFormModal
        visible={formMode !== null}
        mode={formMode ?? 'new'}
        source={formSource}
        onCancel={() => {
          setFormMode(null);
          setFormSource(undefined);
        }}
        onSuccess={result => {
          const label =
            formMode === 'update'
              ? intl.formatMessage({ id: 'ApiKeys.modal.apiKeys.edited' })
              : formMode === 'rotate'
                ? intl.formatMessage({ id: 'ApiKeys.rotate.success' })
                : intl.formatMessage({ id: 'ApiKeys.success.message' });
          onCreatedOrUpdated(result, label);
        }}
      />

      <StyledModal
        visible={!!generatedKey}
        footer={null}
        closable={false}
        destroyOnClose
      >
        <GeneratedAPIKeyInstance
          apiKey={generatedKey}
          onClose={() => {
            setGeneratedKey('');
            refresh();
          }}
        />
      </StyledModal>

      <StyledModal
        visible={!!deactivateTarget}
        footer={null}
        closable={false}
        destroyOnClose
      >
        {deactivateTarget && (
          <DeactivateAPIKeyInstanceForm
            name={deactivateTarget.name}
            apiKey={deactivateTarget.masked}
            startDate={deactivateTarget.activeFrom}
            endDate={deactivateTarget.expireAt}
            lastUsedAt={deactivateTarget.lastUsedAt}
            status={deactivateTarget.status}
            onDeactivate={handleDeactivate}
            onCancel={() => setDeactivateTarget(null)}
          />
        )}
      </StyledModal>
    </PageContainer>
  );
};

export default APIKey;
